import { useState, useCallback } from 'react';
import { useSnackbar } from 'hooks/useSnackbar';
import { useRestApi } from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { useTranslation } from 'react-i18next';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { handleApiError, isBusinessError } from '../utils';

interface ExecutionWarning {
  sitesPassedValidation: Array<{
    captain_id: string;
    tenant_id: string;
    learn_url: string;
    timezone: string;
  }>;
  sitesFailedValidation: Array<{
    captain_id: string;
    tenant_id: string;
    reason: string;
  }>;
  message: string;
}

interface ExecutionResult {
  success: boolean;
  hasWarnings: boolean;
  warning?: ExecutionWarning;
  error?: string;
}

export const useCohortExecution = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { selectedRegion } = useCohortsContext();

  const [isExecuting, setIsExecuting] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [retryingSiteIds, setRetryingSiteIds] = useState<string[]>([]);

  const executeApi = useRestApi('', { manual: true });
  const cancelApi = useRestApi('', { manual: true });
  const retryApi = useRestApi('', { manual: true });

  const executeCohort = useCallback(
    async (cohortId: string): Promise<ExecutionResult> => {
      setIsExecuting(true);

      const executeUrl = `${apiUrl('lct', `regions/${selectedRegion}/cohort`)}/${cohortId}/execute`;

      try {
        await executeApi.fetch({
          url: executeUrl,
          method: 'POST',
          data: {},
        });

        enqueueSnackbar(t('cohorts.executionStartedSuccessfully'), { variant: 'success' });
        return {
          success: true,
          hasWarnings: false,
        };
      } catch (error: any) {
        if (isBusinessError(error, 422, 'SITES_VALIDATION_FAILED')) {
          enqueueSnackbar(t('cohorts.executionWithWarnings'), { variant: 'warning' });
          return {
            success: true,
            hasWarnings: true,
            warning: {
              sitesPassedValidation: error.response.data.sites_passed_validation || [],
              sitesFailedValidation: error.response.data.sites_failed_validation || [],
              message: error.response.data.message || '',
            },
          };
        }

        handleApiError(error, { operation: 'executeCohort', enqueueSnackbar, t });
        return {
          success: false,
          hasWarnings: false,
          error: error?.response?.data?.message || error?.message || t('cohorts.executionFailed'),
        };
      } finally {
        setIsExecuting(false);
      }
    },
    [executeApi, enqueueSnackbar, t, selectedRegion],
  );

  const cancelExecution = useCallback(
    async (cohortId: string): Promise<boolean> => {
      setIsCanceling(true);

      const cancelUrl = `${apiUrl('lct', `regions/${selectedRegion}/cohort`)}/${cohortId}/cancel`;

      try {
        await cancelApi.fetch({
          url: cancelUrl,
          method: 'POST',
          data: {},
        });

        enqueueSnackbar(t('cohorts.executionCancelledSuccessfully'), { variant: 'success' });
        return true;
      } catch (error: any) {
        handleApiError(error, { operation: 'cancelExecution', enqueueSnackbar, t });
        return false;
      } finally {
        setIsCanceling(false);
      }
    },
    [cancelApi, enqueueSnackbar, t, selectedRegion],
  );

  const retrySiteExecution = useCallback(
    async (cohortId: string, siteId: string, delayHours: number): Promise<boolean> => {
      setRetryingSiteIds((previous) =>
        previous.includes(siteId) ? previous : [...previous, siteId],
      );

      const retryUrl = `${apiUrl('lct', `regions/${selectedRegion}/site`)}/${siteId}/execute`;

      try {
        await retryApi.fetch({
          url: retryUrl,
          method: 'POST',
          data: { delay_hours: delayHours },
        });

        enqueueSnackbar(t('cohorts.siteRetryStartedSuccessfully'), { variant: 'success' });
        return true;
      } catch (error: any) {
        handleApiError(error, { operation: 'retrySiteExecution', enqueueSnackbar, t });
        setRetryingSiteIds((previous) => previous.filter((id) => id !== siteId));
        return false;
      }
    },
    [retryApi, enqueueSnackbar, t, selectedRegion],
  );

  const clearRetryState = useCallback((siteId: string) => {
    setRetryingSiteIds((previous) => previous.filter((id) => id !== siteId));
  }, []);

  const isSiteRetrying = useCallback(
    (siteId: string) => retryingSiteIds.includes(siteId),
    [retryingSiteIds],
  );

  return {
    executeCohort,
    cancelExecution,
    retrySiteExecution,
    clearRetryState,
    isSiteRetrying,
    isExecuting,
    isCanceling,
    retryingSiteIds,
  };
};
